import MerchandiseSearchFormResultItem from './MerchandiseSearchFormResultItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'

const ResultsGrid = styled.div`
  display: grid;

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(40)}
  }
`

interface Props {
  text?: string
  results: App.MerchandiseSearchTypeahead
  loading?: boolean
  setClosed?: () => void
}

function MerchandiseSearchFormResults(props: Props) {
  const { results, text, loading, setClosed } = props
  const intl = useIntl()

  const trackingProductConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'product'), { key: 'product' },
  )
  const trackingCategoryConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'category'), { key: 'category' },
  )
  const trackingBrandConfig = useOfferListTracking(
    getListName('OfferList', 'Merchandise Search Form Results', 'brand'), { key: 'brand' },
  )

  return <VerticalSpacer gap={24}>
    {text && <Caption variant="large" weight="bold">
      {intl.formatMessage(
        {
          id: 'Q7-Etw5qwy6Kwv-6vsNR',
          defaultMessage: 'Results for "{searchText}"',
          description: 'Header showing search results for a specific query',
        },
        { searchText: text },
      )}
    </Caption>}
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: 'VGP2PhM2dhfqz2jmtbDe',
          defaultMessage: 'Recommended products',
          description: 'Section title for recommended products in search results',
        })}
      </Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingProductConfig}>
          {take(results.products, 3).map((product, index) => (
            <MerchandiseSearchFormResultItem
              key={product.id}
              itemId={product.id}
              type="product"
              index={index}
              setClosed={setClosed}
            />
          ))}

          {!results.products.length && <Caption variant="large">
            {intl.formatMessage(
              {
                id: 'YOA4468KjXxS_XASBDnc',
                defaultMessage: 'Sorry, we don’t have any products that relate to "{searchText}"',
                description: 'Message shown when no products match the search query',
              },
              { searchText: text },
            )}
          </Caption>}
        </OfferListEventsProvider>
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: 'xapuYHfcVbNkkSFgDIG1',
          defaultMessage: 'Recommended categories',
          description: 'Section title for recommended categories in search results',
        })}
      </Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingCategoryConfig}>
          {take(results.categories, 3).map((category, index) => (
            <MerchandiseSearchFormResultItem
              key={category.id}
              itemId={category.id}
              type="category"
              index={index}
            />
          ))}
        </OfferListEventsProvider>

        {!results.categories.length && <Caption variant="large">
          {intl.formatMessage(
            {
              id: 'TXLcRVNWe701LnQJ3EfB',
              defaultMessage: 'Sorry, we don’t have any categories that relate to "{searchText}"',
              description: 'Message shown when no categories match the search query',
            },
            { searchText: text },
          )}
        </Caption>}
      </ResultsGrid>
    </VerticalSpacer>
    <VerticalSpacer gap={8}>
      <Caption variant="large" weight="bold">
        {intl.formatMessage({
          id: 'U57x2IZF-RAn2qwZxMG6',
          defaultMessage: 'Recommended brands',
          description: 'Section title for recommended brands in search results',
        })}
      </Caption>
      <ResultsGrid>
        {loading && <>
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
          <ListItemLoadingSkeleton />
        </>}
        <OfferListEventsProvider tracking={trackingBrandConfig}>
          {take(results.brands, 3).map((brand, index) => (
            <MerchandiseSearchFormResultItem
              key={brand.id}
              itemId={brand.id}
              type="brand"
              index={index}
            />
          ))}
        </OfferListEventsProvider>

        {!results.brands.length && <Caption variant="large">
          {intl.formatMessage(
            {
              id: 'zwdAYBEx9wIW0fq4WG-v',
              defaultMessage: 'Sorry, we don’t have any brands that relate to "{searchText}"',
              description: 'Message shown when no brands match the search query',
            },
            { searchText: text },
          )}
        </Caption>}
      </ResultsGrid>
    </VerticalSpacer>
  </VerticalSpacer>
}

export default MerchandiseSearchFormResults
